import { computed, effect, signal } from '@preact/signals';
import type { AvalancheRequest, AvalancheResponse } from '../sim/avalanche/avalanche.worker.ts';
import { applyAvalanche, undoAvalanche, type ApplyResult } from '../sim/avalanche/apply.ts';
import type { FlowResult, FlowStats } from '../sim/avalanche/flow.ts';
import { detrainmentField } from '../sim/forest.ts';
import { defaultFlowParams, FLOW_DEFAULTS, type FlowParams } from '../sim/avalanche/params.ts';
import {
  computeRelease,
  entrainableSnow,
  flowBed,
  RELEASE_MODEL,
  type ReleaseDepthMode,
  type ReleaseResult,
  type ReleaseTrigger,
} from '../sim/avalanche/release.ts';
import type { ScalarField } from '../sim/grid.ts';
import type { TerrainState } from '../sim/scene.ts';
import {
  AVALANCHE_OVERLAY_MODES,
  avalancheDepthDelta,
  forestCover,
  overlay,
  resolutionOverride,
  selectedCell,
  SIMPLE_OVERLAY_MODES,
  snowDepthOnTerrain,
  snowpack,
  snowpackVersion,
  terrain,
  uiMode,
  viewHour,
} from './store.ts';

/**
 * Avalanche state: trigger settings, the hover preview, running the flow in a worker, the run
 * history (each run applied to the snowpack, undoable in reverse order), playback, deposit
 * overlays and the cross-section tool.
 */

export type TriggerMode = 'brush' | 'natural';

export interface TriggerSettings {
  mode: TriggerMode;
  /** Brush radius (m). */
  radius: number;
  depthMode: ReleaseDepthMode;
  fixedCm: number;
  /** Natural trigger: maximum fracture reach (m). */
  maxPropagation: number;
  /** Manual particle mass (kg); null = automatic (75 kg, coarsened to fit the budget). */
  particleMassKg: number | null;
}

export const triggerSettings = signal<TriggerSettings>({
  mode: 'brush',
  radius: 60,
  depthMode: 'slab',
  fixedCm: 50,
  maxPropagation: 300,
  particleMassKg: null,
});

/** The trigger tool is active: hovering previews a release, clicking releases it. */
export const triggerArmed = signal(false);

/** What would release at the currently hovered point, while armed — for the live preview
 * readout in the panel. Written by Scene3D's hover handler. */
export const hoverRelease = signal<ReleaseResult | null>(null);

/** Transient feedback when a click while armed released nothing, or a run couldn't start. */
export const triggerMessage = signal<string | null>(null);

export function armTrigger(): void {
  triggerArmed.value = true;
  crossSectionArmed.value = false;
  crossSectionPending.value = null;
}

export function disarmTrigger(): void {
  triggerArmed.value = false;
}

/**
 * Switch to simple mode: disarm the trigger and cross-section tools, clear the profile
 * selection, fall back to automatic resolution, force particle colouring to speed (the only
 * legend simple mode shows), and land on a simple-mode overlay if the current one isn't one.
 */
export function enterSimpleMode(): void {
  uiMode.value = 'simple';
  disarmTrigger();
  disarmCrossSection();
  selectedCell.value = null;
  resolutionOverride.value = null;
  colorMode.value = 'speed';
  showDeposited.value = true;
  hideParticles.value = false;
  if (!SIMPLE_OVERLAY_MODES.has(overlay.peek())) overlay.value = 'natural';
}

export interface AvalancheRun {
  id: number;
  trigger: ReleaseTrigger;
  release: ReleaseResult;
  params: FlowParams;
  /** Particle mass (kg) that was requested before the budget coarsened it, for the "coarsened to
   * fit the budget" note. */
  requestedParticleMass: number;
  result: FlowResult;
  applied: ApplyResult;
  /** Wall-clock compute time (ms). */
  computeMs: number;
}

export type RunStatus =
  | { kind: 'idle' }
  | { kind: 'running'; simTime: number; moving: number }
  | { kind: 'error'; message: string };

export const runs = signal<AvalancheRun[]>([]);
export const runStatus = signal<RunStatus>({ kind: 'idle' });

/** Run selected for playback/totals/overlays; null follows the latest run. */
export const selectedRunId = signal<number | null>(null);

/** Keep playback frames only for the most recent runs (they are the bulk of the memory). */
const KEEP_FRAMES_FOR_RUNS = 3;

export interface Playback {
  runId: number;
  /** Simulated time shown (s). */
  time: number;
  playing: boolean;
  /** Playback speed relative to real time. */
  speed: number;
}

export const playback = signal<Playback | null>(null);

/** Particle colour mode for the playback view. */
export type ParticleColorMode = 'speed' | 'thickness' | 'pressure' | 'origin' | 'moving';
export const colorMode = signal<ParticleColorMode>('speed');
export const showDeposited = signal(true);
export const hideParticles = signal(false);

export const latestRun = computed(() => {
  const list = runs.value;
  return list.length > 0 ? list[list.length - 1]! : null;
});

/** The run shown in the totals/history/overlay UI: the selected one, or the latest. */
export const selectedRun = computed<AvalancheRun | null>(() => {
  const id = selectedRunId.value;
  const list = runs.value;
  if (id !== null) {
    const found = list.find((r) => r.id === id);
    if (found) return found;
  }
  return list.length > 0 ? list[list.length - 1]! : null;
});

/** Select a run for totals/history/overlays, and start its playback if frames were kept. */
export function selectRun(id: number): void {
  selectedRunId.value = id;
  const run = runs.peek().find((r) => r.id === id);
  if (run && run.result.frames.frames.length > 0) {
    playback.value = { runId: id, time: 0, playing: false, speed: playbackSpeedPreset.peek() };
  } else if (playback.peek()?.runId !== id) {
    playback.value = null;
  }
}

export function setPlaybackPlaying(playing: boolean): void {
  const p = playback.peek();
  if (!p) return;
  // Restart from the top if it finished.
  const run = runs.peek().find((r) => r.id === p.runId);
  const last = run?.result.frames.frames.at(-1)?.t ?? 0;
  playback.value = { ...p, playing, time: playing && p.time >= last ? 0 : p.time };
}

export function setPlaybackTime(time: number): void {
  const p = playback.peek();
  if (p) playback.value = { ...p, time, playing: false };
}

/** Playback speeds offered as presets. */
export const PLAYBACK_SPEEDS = [0.5, 1, 2, 5, 10] as const;
const SPEED_KEY = 'snow-sim.playbackSpeed';
function loadSpeed(): number {
  try {
    const v = Number(localStorage.getItem(SPEED_KEY));
    return (PLAYBACK_SPEEDS as readonly number[]).includes(v) ? v : 1;
  } catch {
    return 1;
  }
}
/** Speed every new run plays at; changing the speed during playback updates it. Remembered. */
export const playbackSpeedPreset = signal<number>(loadSpeed());
effect(() => {
  const v = playbackSpeedPreset.value;
  try {
    localStorage.setItem(SPEED_KEY, String(v));
  } catch {
    // Not remembered without storage.
  }
});

export function setPlaybackSpeed(speed: number): void {
  playbackSpeedPreset.value = speed;
  const p = playback.peek();
  if (p) playback.value = { ...p, speed };
}

// Advance playback time in real time while playing.
let playbackRaf = 0;
let playbackLast = 0;
function playbackTick(now: number): void {
  playbackRaf = 0;
  const p = playback.peek();
  if (!p || !p.playing) return;
  const dt = playbackLast ? (now - playbackLast) / 1000 : 0;
  playbackLast = now;
  const run = runs.peek().find((r) => r.id === p.runId);
  const last = run?.result.frames.frames.at(-1)?.t ?? 0;
  let t = p.time + dt * p.speed;
  let playing = true;
  if (t >= last) {
    t = last;
    playing = false;
  }
  // Schedule the next tick BEFORE writing the signal: the write synchronously runs the effect
  // below, which starts a tick loop whenever none is scheduled. Scheduling afterwards made every
  // tick start a second loop — the number of callbacks doubled each frame and froze the page.
  if (playing) playbackRaf = requestAnimationFrame(playbackTick);
  playback.value = { ...p, time: t, playing };
}
effect(() => {
  const p = playback.value;
  if (p?.playing && !playbackRaf) {
    playbackLast = 0;
    playbackRaf = requestAnimationFrame(playbackTick);
  }
});

/** Build the trigger for a UTM point from the current settings. */
export function triggerAt(x: number, y: number): ReleaseTrigger {
  // Simple mode always lets the snowpack decide (natural release, default fracture reach).
  if (uiMode.peek() === 'simple') {
    return { kind: 'natural', x, y, maxPropagation: RELEASE_MODEL.defaultReach.persistent };
  }
  const s = triggerSettings.value;
  return s.mode === 'brush'
    ? { kind: 'brush', x, y, radius: s.radius, depthMode: s.depthMode, fixedCm: s.fixedCm }
    : { kind: 'natural', x, y, maxPropagation: s.maxPropagation };
}

/** What would release at a point with the current settings (cheap enough for hover). */
export function previewRelease(x: number, y: number): ReleaseResult | null {
  const t = terrain.peek();
  const sp = snowpack.peek();
  if (!t || !sp) return null;
  return computeRelease(t, sp, triggerAt(x, y), forestOn(t));
}

let worker: Worker | null = null;
let nextId = 0;
let running: {
  id: number;
  trigger: ReleaseTrigger;
  release: ReleaseResult;
  params: FlowParams;
  requestedParticleMass: number;
} | null = null;

function avalancheWorker(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL('../sim/avalanche/avalanche.worker.ts', import.meta.url), {
    type: 'module',
  });
  worker.onmessage = (e: MessageEvent<AvalancheResponse>) => {
    const msg = e.data;
    if (!running || msg.id !== running.id) return;
    if (msg.kind === 'progress') {
      runStatus.value = { kind: 'running', simTime: msg.simTime, moving: msg.moving };
      return;
    }
    const job = running;
    running = null;
    if (msg.kind === 'error') {
      runStatus.value = { kind: 'error', message: msg.message };
      dispatchQueue();
      return;
    }
    finishRun(job, msg.result, msg.ms);
  };
  worker.onerror = (e) => {
    running = null;
    runStatus.value = { kind: 'error', message: e.message || 'Avalanche worker failed' };
    triggerQueue.value = [];
  };
  return worker;
}

/**
 * Release and run an avalanche at a UTM point. Returns why nothing released, or null if a run
 * was started.
 */
export function triggerAvalanche(x: number, y: number): string | null {
  if (!terrain.peek() || !snowpack.peek()) return 'No snowpack yet.';
  // While scrubbing, the view shows an approximate snapshot; avalanches need the exact state.
  if (viewHour.peek() !== null)
    return 'Computing the snowpack at this hour — try again in a moment.';
  const trigger = triggerAt(x, y);
  // One avalanche at a time: later clicks wait their turn, and each one then releases from the
  // snowpack the previous avalanches left behind.
  if (running || triggerQueue.peek().length > 0) {
    triggerQueue.value = [...triggerQueue.peek(), { id: ++nextQueueId, x, y, trigger }];
    return null;
  }
  return startRun(trigger);
}

/** A click waiting for the running avalanche to finish. */
export interface QueuedTrigger {
  id: number;
  x: number;
  y: number;
  trigger: ReleaseTrigger;
}

export const triggerQueue = signal<QueuedTrigger[]>([]);
let nextQueueId = 0;

export function clearTriggerQueue(): void {
  triggerQueue.value = [];
}

/** Start the next queued avalanche, skipping (and reporting) spots where nothing releases now. */
function dispatchQueue(): void {
  while (!running && triggerQueue.peek().length > 0) {
    const [next, ...rest] = triggerQueue.peek();
    triggerQueue.value = rest;
    const reason = startRun(next!.trigger);
    if (reason) triggerMessage.value = `Queued release skipped: ${reason}`;
  }
}

/** Crown cover for the terrain, if SR16 has loaded for it. */
function forestOn(t: TerrainState): Float32Array | null {
  const f = forestCover.peek();
  return f && f.spec === t.plan.terrain ? f.data : null;
}

function startRun(trigger: ReleaseTrigger): string | null {
  const t = terrain.peek();
  const sp = snowpack.peek();
  const depthOnTerrain = snowDepthOnTerrain.peek();
  if (!t || !sp || !depthOnTerrain) return 'No snowpack yet.';
  if (running) return 'An avalanche is still running.';
  const forest = forestOn(t);
  const release = computeRelease(t, sp, trigger, forest);
  if (release.cells.length === 0) return release.reason ?? 'Nothing releases here.';
  const settings = triggerSettings.peek();
  const requestedParticleMass = settings.particleMassKg ?? FLOW_DEFAULTS.particleMass;
  const params = defaultFlowParams(release.flowType, release.volumeM3, release.massKg, {
    ...(release.loose ? FLOW_DEFAULTS.loose : {}),
    ...(settings.particleMassKg ? { particleMass: settings.particleMassKg } : {}),
  });
  const { entrainable, entrainableDensity } = entrainableSnow(t.plan, sp, release);
  const id = ++nextId;
  running = { id, trigger, release, params, requestedParticleMass };
  runStatus.value = { kind: 'running', simTime: 0, moving: release.cells.length };
  const request: AvalancheRequest = {
    id,
    input: {
      spec: t.plan.terrain,
      bed: flowBed(t, depthOnTerrain, release),
      release: release.release,
      releaseDensity: release.releaseDensity,
      entrainable,
      entrainableDensity,
      ...(forest ? { detrainment: detrainmentField(forest) } : {}),
      params,
    },
  };
  avalancheWorker().postMessage(request);
  return null;
}

/** Cumulative deposit depth (m) per terrain cell, summed across all runs (§11). */
export const cumulativeDepositDepthM = signal<Float32Array | null>(null);

/** User has picked one of the avalanche overlay modes themselves — stop auto-switching. */
export const userPickedAvalancheOverlay = signal(false);

function finishRun(
  job: {
    id: number;
    trigger: ReleaseTrigger;
    release: ReleaseResult;
    params: FlowParams;
    requestedParticleMass: number;
  },
  result: FlowResult,
  ms: number,
): void {
  const t = terrain.peek();
  const sp = snowpack.peek();
  if (!t || !sp) {
    runStatus.value = { kind: 'idle' };
    return;
  }
  const applied = applyAvalanche(t.plan, sp, result, job.params.debrisDensity);
  const delta = avalancheDepthDelta.peek() ?? new Float32Array(result.netDepth.length);
  for (let i = 0; i < delta.length; i++) delta[i] = delta[i]! + result.netDepth[i]!;
  avalancheDepthDelta.value = Float32Array.from(delta);

  const dep = cumulativeDepositDepthM.peek() ?? new Float32Array(result.deposit.length);
  const nextDep = Float32Array.from(dep);
  const rho = job.params.debrisDensity;
  for (let i = 0; i < nextDep.length; i++) nextDep[i]! += result.deposit[i]! / rho;
  cumulativeDepositDepthM.value = nextDep;

  const run: AvalancheRun = { ...job, result, applied, computeMs: ms };
  const list = [...runs.peek(), run];
  // Drop playback frames of older runs to bound memory.
  for (let k = 0; k < list.length - KEEP_FRAMES_FOR_RUNS; k++) {
    list[k]!.result.frames.frames.length = 0;
  }
  runs.value = list;
  snowpackVersion.value++;
  runStatus.value = { kind: 'idle' };
  triggerMessage.value = null;
  selectedRunId.value = run.id;
  playback.value = { runId: run.id, time: 0, playing: true, speed: playbackSpeedPreset.peek() };
  if (!userPickedAvalancheOverlay.peek()) overlay.value = 'avNet';
  dispatchQueue();
}

/** Undo the most recent run: restore the snowpack and the depth change. */
export function undoLastRun(): void {
  const list = runs.peek();
  const last = list[list.length - 1];
  const sp = snowpack.peek();
  if (!last || !sp) return;
  undoAvalanche(sp, last.applied.patch);
  const delta = avalancheDepthDelta.peek();
  if (delta) {
    const next = Float32Array.from(delta);
    for (let i = 0; i < next.length; i++) next[i] = next[i]! - last.result.netDepth[i]!;
    avalancheDepthDelta.value = list.length > 1 ? next : null;
  }
  const dep = cumulativeDepositDepthM.peek();
  if (dep) {
    const next = Float32Array.from(dep);
    const rho = last.params.debrisDensity;
    for (let i = 0; i < next.length; i++) next[i]! -= last.result.deposit[i]! / rho;
    cumulativeDepositDepthM.value = list.length > 1 ? next : null;
  }
  runs.value = list.slice(0, -1);
  snowpackVersion.value++;
  if (playback.peek()?.runId === last.id) playback.value = null;
  if (selectedRunId.peek() === last.id) selectedRunId.value = null;
}

/** Summary numbers for the totals panel (the latest run). */
export function runSummary(run: AvalancheRun): FlowStats {
  return run.result.stats;
}

/** Per-cell avalanche data for the active overlay mode (terrain grid), or null when the mode
 * isn't an avalanche overlay or there's nothing to show yet. */
export const avalancheOverlayField = computed<ScalarField | null>(() => {
  const t = terrain.value;
  const mode = overlay.value;
  if (!t || !AVALANCHE_OVERLAY_MODES.has(mode)) return null;
  const spec = t.plan.terrain;
  if (mode === 'avDeposit') {
    const cum = cumulativeDepositDepthM.value;
    return cum ? { spec, data: cum } : null;
  }
  if (mode === 'avNet') {
    const delta = avalancheDepthDelta.value;
    return delta ? { spec, data: delta } : null;
  }
  const run = selectedRun.value;
  if (!run) return null;
  if (mode === 'avOrigin') {
    const n = spec.cols * spec.rows;
    const data = new Float32Array(n);
    const dep = run.result.deposit;
    const depEnt = run.result.depositEntrained;
    for (let i = 0; i < n; i++) data[i] = dep[i]! > 0 ? depEnt[i]! / dep[i]! : NaN;
    return { spec, data };
  }
  if (mode === 'avMaxSpeed') return { spec, data: run.result.maxSpeed };
  if (mode === 'avMaxThickness') return { spec, data: run.result.maxThickness };
  if (mode === 'avMaxPressure') return { spec, data: run.result.maxPressureKPa };
  return null;
});

// ---- Cross-section tool ----------------------------------------------------------------

export interface CrossSectionPoint {
  x: number;
  y: number;
}

export interface CrossSectionLine {
  a: CrossSectionPoint;
  b: CrossSectionPoint;
}

export const crossSectionArmed = signal(false);
/** First point clicked, waiting for the second. */
export const crossSectionPending = signal<CrossSectionPoint | null>(null);
export const crossSectionLine = signal<CrossSectionLine | null>(null);
export const crossSectionExaggeration = signal(3);

export function armCrossSection(): void {
  crossSectionArmed.value = true;
  crossSectionPending.value = null;
  triggerArmed.value = false;
}

export function disarmCrossSection(): void {
  crossSectionArmed.value = false;
  crossSectionPending.value = null;
}

export function clearCrossSection(): void {
  crossSectionArmed.value = false;
  crossSectionPending.value = null;
  crossSectionLine.value = null;
}

/**
 * Route a terrain click to the active tool: avalanche trigger, cross-section point picking, or
 * (neither armed) the ordinary snow-profile cell selection.
 */
export function handleTerrainClick(x: number, y: number, snowpackCell: number | undefined): void {
  // Simple mode: a click on the terrain always means "release here".
  if (uiMode.peek() === 'simple' || triggerArmed.peek()) {
    triggerMessage.value = triggerAvalanche(x, y);
    return;
  }
  if (crossSectionArmed.peek()) {
    const pending = crossSectionPending.peek();
    if (!pending) {
      crossSectionPending.value = { x, y };
    } else {
      crossSectionLine.value = { a: pending, b: { x, y } };
      crossSectionPending.value = null;
      crossSectionArmed.value = false;
    }
    return;
  }
  if (snowpackCell !== undefined) selectedCell.value = snowpackCell;
}

// Clear transient trigger feedback when disarmed.
effect(() => {
  if (!triggerArmed.value) {
    hoverRelease.value = null;
    triggerMessage.value = null;
  }
});

// A new scenario result replaces the snowpack the runs were applied to: they no longer exist.
let lastSnowpack: unknown = null;
effect(() => {
  const sp = snowpack.value;
  if (sp !== lastSnowpack) {
    lastSnowpack = sp;
    if (runs.peek().length > 0) runs.value = [];
    triggerQueue.value = [];
    cumulativeDepositDepthM.value = null;
    selectedRunId.value = null;
    playback.value = null;
    clearCrossSection();
    if (running) {
      running = null;
      runStatus.value = { kind: 'idle' };
    }
  }
});
